import React from 'react';
import { Theme } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/styles';
import FilesClient from '../../clients/FilesClient';
import { ENTITIES, FOLDER_FILE_KEY, User } from '../../constants';
import { InboxPageContext } from '../../context/inboxPageContext';
import {
  getExtensionFromFileKey,
  getFileNameFromFileKey,
} from '../Files/helpers';
import { InboxNotificationTable } from './InboxNotificationTable';
import { LoadingWrapper } from '../Loading';
import InboxClient from '../../clients/InboxClient';
import { InboxEmptyState } from './InboxEmptyState';

export interface NotificationTableRowData extends User {
  rowId: string;
  name: string;
  location: string;
  channelId: string;
  userName: string;
  isDeleted?: boolean;
  fileExtension?: string;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      width: '100%',
      padding: theme.spacing(3, 4.5),
    },
  }),
);

export const DELETED_FILE_NOTIFICATION_STATUS = '0_CANCELLED';

export const InboxContent = () => {
  const classes = useStyles();
  const [loading, setLoading] = React.useState(false);
  const { selectedNotification } = React.useContext(InboxPageContext);
  const isFileUploadNotification =
    selectedNotification &&
    selectedNotification.notificationGroup === ENTITIES.NOTIFY_FILE_UPLOADED;

  const [
    notificationTableRowData,
    setNotificationTableRowData,
  ] = React.useState<NotificationTableRowData[]>([]);

  /**
   * generate row data for notification table
   */
  const generateNotificationTableRowData = React.useCallback(async () => {
    // clearing last selected notification data
    setNotificationTableRowData([]);
    setLoading(true);

    // don't do anything if no notification is selected
    if (!selectedNotification || !selectedNotification.channelId) return;

    const allChannelFiles = await FilesClient.GetFilesForChannelId(
      selectedNotification.channelId,
    );

    const rowData: NotificationTableRowData[] = [];
    const deletedFilesRowData = [];
    // get id's of file related to notification
    let notificationFileIds: string[] = [];

    // for file upload notifications
    if (isFileUploadNotification) {
      // we can retreive file id's from notifications fetched from groupID
      const notificationsFromGroup = await InboxClient.NotificationByGroupId(
        selectedNotification?.id as string,
      );
      notificationsFromGroup?.forEach(n => {
        const fileId = n.data.split('/')[2]; // data string is divided into 3 parts by "/". `${Some_id}/${notification_status}/${fileId}`
        if (fileId === 'default') return; // notification group itself has the 'default' id and we can ignore that

        // checking if the notification file is deleted or not
        if (n.data.includes(DELETED_FILE_NOTIFICATION_STATUS)) {
          // if file is deleted
          // to indicated a deleted file add a row in notification table
          // with name 'File was deleted'
          deletedFilesRowData.push({
            rowId: fileId,
            name: 'File was deleted',
            location: '',
            isDeleted: true,
          });
          return;
        }

        notificationFileIds.push(fileId);
      });
    } else {
      // for notifications which are not grouped
      // we know there will be only 1 file in those notifications
      const fileId = selectedNotification.data.split('/')[2];
      // check if the file is deleted
      if (
        selectedNotification.data.includes(DELETED_FILE_NOTIFICATION_STATUS)
      ) {
        deletedFilesRowData.push({
          rowId: fileId,
          name: 'File was deleted',
          location: '',
          isDeleted: true,
        });
      } else {
        notificationFileIds = [fileId];
      }
    }

    const notificationFiles = allChannelFiles.filter(n =>
      notificationFileIds?.includes(n.id),
    );

    notificationFiles.forEach(file => {
      const { fields, owner } = file;

      // avoiding folders
      if (fields.fileKey === FOLDER_FILE_KEY) return;
      if (!owner) return;

      let fileExtension = '';

      // generating file name
      let name = getFileNameFromFileKey(fields.fileKey);
      // except for file links
      // add file extension in file name
      if (!fields.fileUrl) {
        fileExtension = getExtensionFromFileKey(fields.fileKey); // file extension
        name = `
           ${name}${fileExtension}`;
      }

      if (fields.fileUrl) {
        fileExtension = 'link';
      }

      // for file upload notification
      // we will show file owner info in the "From" column
      // and for Esign complete notification we will show
      // notification sender user info in "Signed by" column
      const userInfo =
        selectedNotification.notificationGroup === ENTITIES.NOTIFY_FILE_UPLOADED
          ? {
              userName: `${owner?.fields.given_name} ${owner?.fields.family_name}`,
              ...owner,
            }
          : {
              userName: selectedNotification.avatarInfo?.name,
              fields: {
                picture: selectedNotification.avatarInfo?.imageUrl,
                ...selectedNotification.avatarInfo,
              },
            };

      const item = {
        rowId: file.id,
        name,
        fileExtension: fileExtension.replace('.', '').toLowerCase(), // removing "." from extension names e.g ".pdf" > "pdf"
        location: file.fields.path || '',
        channelId: selectedNotification.channelId,
        email: userInfo.fields.email,
        fallbackColor: userInfo.fields.fallbackColor,
        picture: userInfo.fields.picture,
        ...userInfo,
      };

      rowData.push(item);
    });

    setNotificationTableRowData([...rowData, ...deletedFilesRowData]);
    setLoading(false);
  }, [selectedNotification]);

  React.useEffect(() => {
    generateNotificationTableRowData();
  }, [selectedNotification]);

  // when no notification is selected
  // render emtpy page
  if (!selectedNotification) return <InboxEmptyState />;

  return (
    <div
      className={classes.container}
      data-testid="inbox-selected-notification"
    >
      <LoadingWrapper isLoading={loading} hideContentWhileLoading fullHeight>
        {notificationTableRowData && notificationTableRowData.length > 0 && (
          <InboxNotificationTable
            rowData={notificationTableRowData}
            notificationGroup={selectedNotification.notificationGroup}
          />
        )}
      </LoadingWrapper>
    </div>
  );
};
